<script setup lang="ts">
const props = defineProps<{ data: TimelineProps; template?: 'page' | 'overlay' }>()

const $buttons = useTemplateRef('$buttons')

const activeIndex = ref(0)

const activeItem = computed(() => {
  return props.data.timelineItems?.[activeIndex.value]
})

const direction = ref<1 | -1>(1)

function navigate(newDirection: -1 | 1) {
  direction.value = newDirection

  if (newDirection === 1) {
    activeIndex.value = Math.min(activeIndex.value + 1, props.data.timelineItems.length - 1)
  }

  if (newDirection === -1) {
    activeIndex.value = Math.max(activeIndex.value - 1, 0)
  }

  if ($buttons.value) {
    const activeButton = $buttons.value[activeIndex.value]
    activeButton?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }
}

function navigateTo(targetIndex: number) {
  if (targetIndex === activeIndex.value) return

  direction.value = targetIndex > activeIndex.value ? 1 : -1
  activeIndex.value = targetIndex

  if ($buttons.value) {
    const activeButton = $buttons.value[activeIndex.value]
    activeButton?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }
}

const contentWrapper = ref<HTMLElement | null>(null)
const activeSlide = ref<HTMLElement | null>(null)
const containerHeight = ref('auto')

function updateHeight() {
  const height = activeSlide.value?.offsetHeight
  if (height) {
    containerHeight.value = `${height}px`
  }
}

onMounted(async () => {
  await nextTick()
  updateHeight()
})
</script>

<template>
  <section
    class="py-5"
    :class="{
      'bg-white': template !== 'page' && template !== 'overlay' && data.theme !== 'light-grey',
      'bg-light-grey':
        template === 'page' || (template !== 'overlay' && data.theme === 'light-grey'),
    }"
  >
    <header class="px-bleed gap-x-bleed grid items-end gap-y-5 lg:grid-cols-2">
      <nav class="flex gap-2">
        <button
          aria-label="Slide to the previous item"
          class="flex size-10 cursor-pointer items-center justify-center bg-(--page-cta-bg) text-(--page-cta-text) transition-opacity lg:size-12"
          :class="{ 'pointer-events-none opacity-50': activeIndex === 0 }"
          @click="navigate(-1)"
        >
          <IconsArrowE class="h-6 lg:h-8" />
        </button>
        <button
          aria-label="Slide to the next item"
          class="flex size-10 cursor-pointer items-center justify-center bg-(--page-cta-bg) text-(--page-cta-text) transition-opacity lg:size-12"
          :class="{
            'pointer-events-none opacity-50': activeIndex === data.timelineItems.length - 1,
          }"
          @click="navigate(1)"
        >
          <IconsArrowW class="h-6 lg:h-8" />
        </button>
      </nav>

      <div
        ref="contentWrapper"
        class="relative flex flex-col gap-5 overflow-hidden transition-[height] duration-500 ease-in-out"
        :style="{ height: containerHeight }"
      >
        <transition
          :name="direction === 1 ? 'slide-left' : 'slide-right'"
          mode="out-in"
          @enter="updateHeight"
        >
          <div :key="activeItem?.title" ref="activeSlide" class="relative flex flex-col gap-5">
            <p
              lang="en"
              class="font-display max-w-[12em] text-3xl wrap-break-word hyphens-auto uppercase font-stretch-expanded lg:text-4xl"
            >
              {{ activeItem?.title }}
            </p>
            <ElementsPortableTextWrapper
              :key="activeItem?.text?.[0]?._key"
              class="text-lg"
              :content="activeItem?.text"
            />
          </div>
        </transition>
      </div>
    </header>

    <div class="mx-bleed mt-8 border-t border-black/50 lg:mt-10" />

    <ul class="px-bleed no-scrollbar scroll-p-bleed flex overflow-auto py-4">
      <li
        v-for="(item, idx) in data.timelineItems"
        :key="idx"
        ref="$buttons"
        :class="activeIndex !== idx ? 'opacity-30' : ''"
        class="pr-bleed w-[calc(100%/2-var(--grid-gap))] shrink-0 transition-opacity lg:w-[calc(100%/4-var(--grid-gap)/3)]"
      >
        <button class="flex cursor-pointer flex-col items-start gap-2" @click="navigateTo(idx)">
          <span class="font-mono text-sm uppercase">{{ item.month }}</span>
          <span class="font-display text-xl uppercase font-stretch-expanded">{{ item.year }}</span>
        </button>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.slide-left-enter-active,
.slide-right-enter-active,
.slide-left-leave-active,
.slide-right-leave-active {
  transition: all 0.4s ease;
  position: absolute;
  width: 100%;
}

.slide-left-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.slide-left-enter-to {
  transform: translateX(0%);
  opacity: 1;
}
.slide-left-leave-from {
  transform: translateX(0%);
  opacity: 1;
}
.slide-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.slide-right-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}
.slide-right-enter-to {
  transform: translateX(0%);
  opacity: 1;
}
.slide-right-leave-from {
  transform: translateX(0%);
  opacity: 1;
}
.slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
